import { useEffect, useMemo, useState } from 'react';

import { ApiError } from '@shared/api/client';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { formatNumber } from '@shared/format';
import { EmptyState } from '@shared/ui/EmptyState';
import { ErrorBanner } from '@shared/ui/ErrorBanner';
import { VocabularyPicker, type PickerOption } from '@shared/ui/picker';
import { Skeleton } from '@shared/ui/Skeleton';
import { useToast } from '@shared/ui/toast/ToastProvider';

import { copy } from './copy';
import { errors } from './errors';
import { useReplaceTagStrip } from './mutations';
import { useTags, useTagStrip } from './queries';

/**
 * The curated tag chip strip, managed beside the two home compositions rather
 * than on a screen of its own.
 *
 * **Why it lives on this page.** The strip is part of what a home page is made
 * of, and the operator arranging that composition is the one deciding which
 * vocabulary sits above it. A second management screen for one ordered list of
 * terms is a screen nobody opens twice a year, and the taxonomy page next door
 * is about the vocabulary itself — what a tag is called and whether it exists —
 * not about which of them a reader is offered.
 *
 * **The uncurated state is stated, not implied.** An uncurated strip draws the
 * whole vocabulary, so a picker starting empty would read as «the strip is
 * empty» when it is the opposite. The service sends `isCurated` precisely
 * because the two cases are the same list, and the line above the picker says
 * which one this is.
 *
 * **The draft is local until saved.** Every other write on this page commits on
 * the action — a shelf is created, moved, deleted — because each of those is
 * one decision. Curating a strip is several: an operator adds four tags and
 * removes one before they have decided anything, and a request per chip would
 * publish every intermediate strip to every reader.
 */
export function TagStripSection() {
  const strip = useTagStrip();
  const tags = useTags();
  const replaceStrip = useReplaceTagStrip();
  const toast = useToast();

  const [draft, setDraft] = useState<string[] | null>(null);

  const saved = useMemo(
    () => (strip.data?.isCurated ? strip.data.items.map((entry) => entry.id) : []),
    [strip.data],
  );

  // `null` means «not edited since the last answer», which is what lets a
  // refetch land without discarding what the operator has typed into the
  // picker. It is cleared when the server's answer itself changes — a save, a
  // refetch that saw a different strip, another operator's write.
  useEffect(() => {
    setDraft(null);
  }, [saved]);

  const value = draft ?? saved;
  const dirty = draft !== null && !sameOrder(draft, saved);

  const options: readonly PickerOption[] = useMemo(
    () => (tags.data?.items ?? []).map((tag) => ({ id: tag.id, label: tag.name })),
    [tags.data],
  );

  const readError = strip.error ?? tags.error;

  function save() {
    replaceStrip.mutate(value, {
      onSuccess: () => {
        setDraft(null);
        toast.show(copy.strip.saved, 'success');
      },
      onError: (error) => {
        const status = error instanceof ApiError ? error.status : 0;
        const code = error instanceof ApiError ? error.code : undefined;
        toast.show(resolveErrorCopy(code, status, errors).message, 'danger');
      },
    });
  }

  return (
    <section aria-labelledby="tag-strip" className="mt-section-gap">
      <h2 id="tag-strip" className="mb-4 font-display text-h2 font-bold">
        {copy.strip.heading}
      </h2>

      <p className="mb-4 mt-3 max-w-measure text-xs text-ink-muted">{copy.strip.context}</p>

      {readError ? <SectionError error={readError} onRetry={() => void strip.refetch()} /> : null}

      {strip.isPending || tags.isPending ? (
        <>
          <p role="status" className="sr-only">
            {copy.strip.loading}
          </p>
          <Skeleton className="h-8 w-full max-w-measure" />
        </>
      ) : null}

      {strip.data && tags.data ? (
        options.length === 0 ? (
          <EmptyState message={copy.strip.empty} />
        ) : (
          <div className="grid max-w-measure gap-3">
            {/* Which of the two states this is — the picker alone cannot say. */}
            <p className="mb-0 text-xs text-ink-muted">
              {strip.data.isCurated
                ? copy.strip.curated(formatNumber(strip.data.items.length))
                : copy.strip.uncurated(formatNumber(options.length))}
            </p>

            <VocabularyPicker
              // `multi`, and the order is the meaning: a chip joins the end of
              // the strip, exactly as a title joins the end of a hand-picked set.
              mode="multi"
              options={options}
              value={value}
              onChange={setDraft}
              disabled={replaceStrip.isPending}
              copy={{
                searchLabel: copy.dialog.params.TAG.searchLabel,
                searchPlaceholder: copy.dialog.params.TAG.searchPlaceholder,
                resultCount: copy.dialog.picker.resultCount,
                noResults: copy.dialog.picker.noResults,
                remove: copy.dialog.picker.remove,
              }}
            />

            <p className="mb-0 text-xs text-ink-muted">{copy.strip.hint}</p>
            <p className="mb-0 text-xs text-ink-muted">{copy.strip.clearHint}</p>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                disabled={!dirty || replaceStrip.isPending}
                onClick={save}
                className="hit-target rounded-sm border border-border-strong bg-surface-raised px-4 text-xs font-semibold text-ink hover:bg-surface-overlay disabled:cursor-not-allowed disabled:opacity-45"
              >
                {copy.strip.save}
              </button>
              <button
                type="button"
                disabled={!dirty || replaceStrip.isPending}
                onClick={() => setDraft(null)}
                className="hit-target rounded-sm px-4 text-xs text-ink hover:bg-hover-bg disabled:cursor-not-allowed disabled:opacity-45"
              >
                {copy.strip.revert}
              </button>
            </div>
          </div>
        )
      ) : null}
    </section>
  );
}

/** Order is meaning here, so a reordered set is a changed set. */
function sameOrder(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}

function SectionError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const status = error instanceof ApiError ? error.status : 0;
  const code = error instanceof ApiError ? error.code : undefined;
  const resolved = resolveErrorCopy(code, status, errors);

  return (
    <ErrorBanner
      message={resolved.message}
      tone={resolved.tone === 'danger' ? 'danger' : 'neutral'}
      onRetry={onRetry}
    />
  );
}
